import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Wrench, Truck, Plug, Droplets, HardHat, Package, Shield, Zap, Building2,
  CheckCircle2, ArrowRight, Eye, EyeOff, Loader2, Star, DollarSign,
  Clock, MapPin, Award, Users, TrendingUp, ChevronRight, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
const STERILEMATE_LOGO = "/logos/sterilemate-white.png?v=4";

const TRADES = [
  { value: "electrical", label: "Electrician" },
  { value: "plumbing", label: "Plumber" },
  { value: "general_contracting", label: "General Contractor" },
  { value: "construction", label: "Construction" },
  { value: "other", label: "Other Trade" },
];

const SUPPLIER_TYPES = [
  { value: "medical_supplies", label: "Medical Supplies" },
  { value: "janitorial", label: "Janitorial / EVS" },
  { value: "water_purification", label: "Water Purification" },
  { value: "construction_materials", label: "Construction Materials" },
  { value: "ppe", label: "PPE & Safety" },
  { value: "other", label: "Other" },
];

const FACILITY_TYPES = [
  { value: "Hospital", label: "Hospital" },
  { value: "ASC", label: "Ambulatory Surgery Center" },
  { value: "Clinic", label: "Clinic / Outpatient" },
  { value: "SNF", label: "Skilled Nursing Facility" },
  { value: "Other", label: "Other" },
];

export default function JoinNetworkPage() {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const defaultTab = typeParam === "supplier" ? "supplier"
    : typeParam === "hospital" ? "hospital"
    : "contractor";
  const navigate = useNavigate();
  const [tab, setTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [cf, setCf] = useState({
    business_name: "", contact_name: "", email: "", phone: "", password: "",
    trade: "", city: "Houston", state: "TX", license_number: "", service_area: "",
    years_experience: "", referral_code: searchParams.get("ref") || "",
  });

  const [sf, setSf] = useState({
    company_name: "", contact_name: "", email: "", phone: "", password: "",
    business_type: "", city: "Houston", state: "TX", product_categories: "",
    referral_code: searchParams.get("ref") || "",
  });

  // Hospital form state
  const [hf, setHf] = useState({
    hospital_name: "", contact_name: "", contact_title: "", email: "",
    phone: "", password: "", website: "", city: "Houston", state: "TX",
    facility_type: "Hospital", bed_count: "",
  });
  const [hospitalAgreed, setHospitalAgreed] = useState(false);

  const handleHospitalSubmit = async (e) => {
    e.preventDefault();
    if (!hf.hospital_name || !hf.contact_name || !hf.email || !hf.phone || !hf.password || !hf.website) {
      toast.error("Please fill in all required fields"); return;
    }
    if (hf.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!hospitalAgreed) {
      toast.error("Please agree to the Sterile Mate LLC partnership terms"); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/hospitals/register-self`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...hf,
          bed_count: hf.bed_count ? parseInt(hf.bed_count) : null,
        }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(true); toast.success(data.message); }
      else { toast.error(data.detail || "Registration failed"); }
    } catch { toast.error("Network error. Please try again."); }
    finally { setSubmitting(false); }
  };

  const handleContractorSubmit = async (e) => {
    e.preventDefault();
    if (!cf.business_name || !cf.contact_name || !cf.email || !cf.phone || !cf.password || !cf.trade) {
      toast.error("Please fill in all required fields"); return;
    }
    if (cf.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contractors/register-self`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...cf, years_experience: cf.years_experience ? parseInt(cf.years_experience) : null }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(true); toast.success(data.message); }
      else { toast.error(data.detail || "Registration failed"); }
    } catch { toast.error("Network error. Please try again."); }
    finally { setSubmitting(false); }
  };

  const handleSupplierSubmit = async (e) => {
    e.preventDefault();
    if (!sf.company_name || !sf.contact_name || !sf.email || !sf.phone || !sf.password || !sf.business_type) {
      toast.error("Please fill in all required fields"); return;
    }
    if (sf.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/suppliers/register-self`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sf),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(true); toast.success(data.message); }
      else { toast.error(data.detail || "Registration failed"); }
    } catch { toast.error("Network error. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (success) {
    const loginRoute = tab === "hospital"
      ? "/hospital/login"
      : tab === "contractor" ? "/contractor/login" : "/supplier/login";
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-emerald-500/20 border border-emerald-500/40 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-black text-white mb-4">Welcome to the Network!</h1>
          <p className="text-slate-400 mb-6">
            Your account is <strong className="text-emerald-300">active right now</strong>. We&apos;ve sent a welcome letter to your inbox explaining how the Sterile Mate LLC / AskIsaac relationship works — please take a minute to read it.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/"><Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800">Back to Home</Button></Link>
            <Link to={loginRoute}><Button className="bg-[#0056A8] hover:bg-[#003366] text-white">Sign In Now</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]" data-testid="join-network-page">
      <div className="bg-gradient-to-r from-[#003366] via-[#0056A8] to-[#003366] border-b border-blue-500/20">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-12 w-auto" />
            <div className="w-px h-10 bg-white/20" />
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-9 w-auto" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-2">Join the Sterile Mate Network</h1>
          <p className="text-blue-200 mb-6">Register in 2 minutes. Your account goes live instantly.</p>

          {/* Step-by-step instructions */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
            {[
              { num: "1", text: "Fill out the form below" },
              { num: "2", text: "Account activates instantly" },
              { num: "3", text: "Get started in your dashboard" },
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center gap-2">
                {idx > 0 && <div className="hidden sm:block w-6 h-px bg-blue-400/40" />}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full">
                  <span className="w-5 h-5 bg-cyan-400 text-[#003366] rounded-full flex items-center justify-center text-xs font-black">{step.num}</span>
                  <span className="text-xs sm:text-sm font-medium text-white">{step.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Why Join?</h3>
              <div className="space-y-3">
                {[
                  { icon: DollarSign, text: "Get paid for hospital jobs" },
                  { icon: Zap, text: "Receive matched job alerts" },
                  { icon: Shield, text: "Earn Fortress Certification" },
                  { icon: Users, text: "Join 7+ health system network" },
                  { icon: Clock, text: "24/7 emergency job opportunities" },
                  { icon: TrendingUp, text: "Grow your healthcare portfolio" },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-sm text-slate-300">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <h4 className="text-sm font-bold text-white mb-3">Certification Tiers</h4>
              <div className="space-y-2">
                {[
                  { tier: "Bronze", desc: "New member — job access", color: "text-amber-600" },
                  { tier: "Silver", desc: "5+ jobs — priority alerts", color: "text-slate-400" },
                  { tier: "Gold", desc: "20+ jobs — premium rates", color: "text-amber-400" },
                  { tier: "Fortress", desc: "Elite — first access to all jobs", color: "text-cyan-400" },
                ].map((t) => (
                  <div key={t.tier} className="flex items-center gap-2">
                    <Star className={`w-4 h-4 ${t.color}`} />
                    <span className="text-sm text-white font-medium">{t.tier}</span>
                    <span className="text-xs text-slate-500">— {t.desc}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500">Already have an account?</p>
              <div className="flex gap-2 justify-center mt-2 flex-wrap">
                <Link to="/hospital/login" className="text-xs text-cyan-400 hover:text-cyan-300">Hospital Login</Link>
                <span className="text-xs text-slate-600">|</span>
                <Link to="/contractor/login" className="text-xs text-cyan-400 hover:text-cyan-300">Contractor Login</Link>
                <span className="text-xs text-slate-600">|</span>
                <Link to="/supplier/login" className="text-xs text-cyan-400 hover:text-cyan-300">Supplier Login</Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-3 mb-6 bg-slate-800/50 border border-slate-700/50 p-1 rounded-lg h-12">
                <TabsTrigger value="hospital" className="rounded-md text-sm font-semibold text-slate-400 data-[state=active]:bg-[#0056A8] data-[state=active]:text-white" data-testid="tab-hospital-reg"><Building2 className="w-4 h-4 mr-2" /> Hospital</TabsTrigger>
                <TabsTrigger value="contractor" className="rounded-md text-sm font-semibold text-slate-400 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-contractor-reg"><Wrench className="w-4 h-4 mr-2" /> Contractor</TabsTrigger>
                <TabsTrigger value="supplier" className="rounded-md text-sm font-semibold text-slate-400 data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="tab-supplier-reg"><Truck className="w-4 h-4 mr-2" /> Supplier</TabsTrigger>
              </TabsList>

              <TabsContent value="hospital">
                <form onSubmit={handleHospitalSubmit} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-4" data-testid="hospital-reg-form">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-200">
                    <strong className="text-white">Business email required.</strong> Your email domain must match your facility&apos;s website
                    (e.g. <code className="text-cyan-300">supply@memorialhermann.org</code> for <code className="text-cyan-300">memorialhermann.org</code>).
                    Personal inboxes (gmail, yahoo, outlook.com) are not accepted.
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Facility Name *</Label><Input value={hf.hospital_name} onChange={(e) => setHf({...hf, hospital_name: e.target.value})} placeholder="Memorial Hermann" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="hospital-name" /></div>
                    <div><Label className="text-slate-300 text-xs">Facility Type *</Label><Select value={hf.facility_type} onValueChange={(v) => setHf({...hf, facility_type: v})}><SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-white" data-testid="hospital-facility-type"><SelectValue /></SelectTrigger><SelectContent>{FACILITY_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent></Select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Your Name *</Label><Input value={hf.contact_name} onChange={(e) => setHf({...hf, contact_name: e.target.value})} placeholder="Jane Smith" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="hospital-contact-name" /></div>
                    <div><Label className="text-slate-300 text-xs">Your Title</Label><Input value={hf.contact_title} onChange={(e) => setHf({...hf, contact_title: e.target.value})} placeholder="Supply Chain Director" className="mt-1 bg-slate-900 border-slate-700 text-white" data-testid="hospital-title" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Facility Website *</Label><div className="relative mt-1"><Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" /><Input value={hf.website} onChange={(e) => setHf({...hf, website: e.target.value})} placeholder="memorialhermann.org" className="pl-9 bg-slate-900 border-slate-700 text-white" required data-testid="hospital-website" /></div></div>
                    <div><Label className="text-slate-300 text-xs">Business Email *</Label><Input type="email" value={hf.email} onChange={(e) => setHf({...hf, email: e.target.value})} placeholder={hf.website ? `you@${hf.website.replace(/^https?:\/\//, "").replace(/^www\./, "")}` : "you@yourhospital.org"} className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="hospital-email" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label className="text-slate-300 text-xs">Phone *</Label><Input value={hf.phone} onChange={(e) => setHf({...hf, phone: e.target.value})} placeholder="(281) 555-0123" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="hospital-phone" /></div>
                    <div><Label className="text-slate-300 text-xs">City</Label><Input value={hf.city} onChange={(e) => setHf({...hf, city: e.target.value})} className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                    <div><Label className="text-slate-300 text-xs">State</Label><Input value={hf.state} onChange={(e) => setHf({...hf, state: e.target.value})} className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Bed Count / Size</Label><Input type="number" value={hf.bed_count} onChange={(e) => setHf({...hf, bed_count: e.target.value})} placeholder="250" className="mt-1 bg-slate-900 border-slate-700 text-white" data-testid="hospital-bed-count" /></div>
                    <div><Label className="text-slate-300 text-xs">Password * (min 8 chars)</Label><div className="relative mt-1"><Input type={showPassword ? "text" : "password"} value={hf.password} onChange={(e) => setHf({...hf, password: e.target.value})} className="bg-slate-900 border-slate-700 text-white pr-10" required data-testid="hospital-password" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
                  </div>

                  <label className="flex items-start gap-3 bg-slate-900/60 border border-slate-700 rounded-lg p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hospitalAgreed}
                      onChange={(e) => setHospitalAgreed(e.target.checked)}
                      className="mt-1"
                      data-testid="hospital-agreement"
                    />
                    <span className="text-xs text-slate-300 leading-relaxed">
                      I agree to engage in business with <strong className="text-white">Sterile Mate LLC</strong> using its
                      intelligence platform, <strong className="text-cyan-300">AskIsaac</strong>. All supply orders,
                      services, and invoices processed through this platform are transacted directly with Sterile Mate LLC.
                      AskIsaac is the visibility/dispatch layer; Sterile Mate LLC is the vendor of record.
                    </span>
                  </label>

                  <Button type="submit" disabled={submitting} className="w-full bg-[#0056A8] hover:bg-[#003366] text-white h-12 text-base font-semibold" data-testid="hospital-submit-btn">
                    {submitting
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Account...</>
                      : <><Building2 className="w-4 h-4 mr-2" />Create Hospital Account</>}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="contractor">
                <form onSubmit={handleContractorSubmit} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-4" data-testid="contractor-reg-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Business Name *</Label><Input value={cf.business_name} onChange={(e) => setCf({...cf, business_name: e.target.value})} placeholder="ABC Electric LLC" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="contractor-business-name" /></div>
                    <div><Label className="text-slate-300 text-xs">Your Name *</Label><Input value={cf.contact_name} onChange={(e) => setCf({...cf, contact_name: e.target.value})} placeholder="John Smith" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="contractor-contact-name" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Email *</Label><Input type="email" value={cf.email} onChange={(e) => setCf({...cf, email: e.target.value})} placeholder="john@abcelectric.com" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="contractor-email" /></div>
                    <div><Label className="text-slate-300 text-xs">Phone *</Label><Input value={cf.phone} onChange={(e) => setCf({...cf, phone: e.target.value})} placeholder="(281) 555-0123" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="contractor-phone" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Trade / Specialty *</Label><Select value={cf.trade} onValueChange={(v) => setCf({...cf, trade: v})}><SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-white" data-testid="contractor-trade"><SelectValue placeholder="Select trade..." /></SelectTrigger><SelectContent>{TRADES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent></Select></div>
                    <div><Label className="text-slate-300 text-xs">License Number</Label><Input value={cf.license_number} onChange={(e) => setCf({...cf, license_number: e.target.value})} placeholder="TX-12345" className="mt-1 bg-slate-900 border-slate-700 text-white" data-testid="contractor-license" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label className="text-slate-300 text-xs">City</Label><Input value={cf.city} onChange={(e) => setCf({...cf, city: e.target.value})} className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                    <div><Label className="text-slate-300 text-xs">State</Label><Input value={cf.state} onChange={(e) => setCf({...cf, state: e.target.value})} className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                    <div><Label className="text-slate-300 text-xs">Years Exp.</Label><Input type="number" value={cf.years_experience} onChange={(e) => setCf({...cf, years_experience: e.target.value})} placeholder="5" className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                  </div>
                  <div><Label className="text-slate-300 text-xs">Password * (min 8 chars)</Label><div className="relative mt-1"><Input type={showPassword ? "text" : "password"} value={cf.password} onChange={(e) => setCf({...cf, password: e.target.value})} className="bg-slate-900 border-slate-700 text-white pr-10" required data-testid="contractor-password" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
                  <div><Label className="text-slate-300 text-xs">Referral Code</Label><Input value={cf.referral_code} onChange={(e) => setCf({...cf, referral_code: e.target.value})} placeholder="Optional" className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                  <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base font-semibold" data-testid="contractor-submit-btn">{submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Account...</> : <><Wrench className="w-4 h-4 mr-2" />Join as Contractor</>}</Button>
                </form>
              </TabsContent>

              <TabsContent value="supplier">
                <form onSubmit={handleSupplierSubmit} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-4" data-testid="supplier-reg-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Company Name *</Label><Input value={sf.company_name} onChange={(e) => setSf({...sf, company_name: e.target.value})} placeholder="MedSupply Co" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="supplier-company-name" /></div>
                    <div><Label className="text-slate-300 text-xs">Contact Name *</Label><Input value={sf.contact_name} onChange={(e) => setSf({...sf, contact_name: e.target.value})} placeholder="Jane Doe" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="supplier-contact-name" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Email *</Label><Input type="email" value={sf.email} onChange={(e) => setSf({...sf, email: e.target.value})} placeholder="jane@medsupply.com" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="supplier-email" /></div>
                    <div><Label className="text-slate-300 text-xs">Phone *</Label><Input value={sf.phone} onChange={(e) => setSf({...sf, phone: e.target.value})} placeholder="(713) 555-0456" className="mt-1 bg-slate-900 border-slate-700 text-white" required data-testid="supplier-phone" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">Business Type *</Label><Select value={sf.business_type} onValueChange={(v) => setSf({...sf, business_type: v})}><SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-white" data-testid="supplier-type"><SelectValue placeholder="Select type..." /></SelectTrigger><SelectContent>{SUPPLIER_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent></Select></div>
                    <div><Label className="text-slate-300 text-xs">Product Categories</Label><Input value={sf.product_categories} onChange={(e) => setSf({...sf, product_categories: e.target.value})} placeholder="Gloves, gowns, PPE..." className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-300 text-xs">City</Label><Input value={sf.city} onChange={(e) => setSf({...sf, city: e.target.value})} className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                    <div><Label className="text-slate-300 text-xs">State</Label><Input value={sf.state} onChange={(e) => setSf({...sf, state: e.target.value})} className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                  </div>
                  <div><Label className="text-slate-300 text-xs">Password * (min 8 chars)</Label><div className="relative mt-1"><Input type={showPassword ? "text" : "password"} value={sf.password} onChange={(e) => setSf({...sf, password: e.target.value})} className="bg-slate-900 border-slate-700 text-white pr-10" required data-testid="supplier-password" /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div></div>
                  <div><Label className="text-slate-300 text-xs">Referral Code</Label><Input value={sf.referral_code} onChange={(e) => setSf({...sf, referral_code: e.target.value})} placeholder="Optional" className="mt-1 bg-slate-900 border-slate-700 text-white" /></div>
                  <Button type="submit" disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 text-base font-semibold" data-testid="supplier-submit-btn">{submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating Account...</> : <><Truck className="w-4 h-4 mr-2" />Join as Supplier</>}</Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
